'use strict';

const Renderer = require('./core/renderer');
const GameLoop = require('./core/game-loop');
const InputManager = require('./core/input');
const StorageService = require('./services/storage');
const CloudService = require('./services/cloud');
const GameConfigService = require('./services/game-config');
const FeedbackService = require('./services/feedback');
const BossAssetService = require('./services/boss-assets');
const LoadingScene = require('./scenes/loading-scene');
const HomeScene = require('./scenes/home-scene');
const LevelSelectScene = require('./scenes/level-select-scene');
const GameScene = require('./scenes/game-scene');
const BossScene = require('./scenes/boss-scene');
const ResultScene = require('./scenes/result-scene');
const DailyChallengesScene = require('./scenes/daily-challenges-scene');
const LeaderboardScene = require('./scenes/leaderboard-scene');
const CosmeticsScene = require('./scenes/cosmetics-scene');
const { CLIENT_VERSION } = require('./config/constants');

class App {
  constructor(options) {
    const settings = options || {};
    this.user = settings.user;
    this.renderer = new Renderer(settings.canvas);
    this.loop = new GameLoop(this.renderer.canvas);
    this.storage = new StorageService(this.user.id);
    this.cloud = new CloudService(settings.apiClient);
    this.gameConfig = new GameConfigService(this.storage, this.cloud);
    this.feedback = new FeedbackService();
    this.bossAssets = new BossAssetService();
    this.profile = this.storage.loadProfile();
    this.profile.nickname = this.user.username;
    this.settings = this.storage.loadSettings();
    this.feedback.setEnabled(this.settings.feedbackEnabled);
    this.pendingRunFinishes = this.storage.loadPendingRunFinishes();
    this.dashboard = this.storage.loadDashboard();
    this.leaderboard = this.storage.loadLeaderboard();
    this.syncElement = settings.syncElement || null;
    this.offline = false;
    this.syncing = false;
    this.currentScene = null;
    this.activeRunContext = null;
    this.elapsed = 0;
    this.transitionToken = 0;
    this.input = new InputManager(this.renderer, (type, point, gesture) => {
      if (type === 'start') this.feedback.unlockFromGesture();
      if (this.currentScene) this.currentScene.handleTouch(type, point, gesture);
    });
    this.scenes = {
      loading: new LoadingScene(this),
      home: new HomeScene(this),
      levelSelect: new LevelSelectScene(this),
      game: new GameScene(this),
      boss: new BossScene(this),
      result: new ResultScene(this),
      dailyChallenges: new DailyChallengesScene(this),
      leaderboard: new LeaderboardScene(this),
      cosmetics: new CosmeticsScene(this)
    };
    this.onHidden = () => {
      this.feedback.setMusicPaused(true);
      if (this.currentScene && this.currentScene.onAppHide) this.currentScene.onAppHide();
    };
    this.onVisible = () => {
      if (this.currentScene && this.currentScene.onAppShow) this.currentScene.onAppShow();
    };
    this.onVisibilityChange = () => {
      if (document.hidden) this.onHidden();
      else this.onVisible();
    };
    this.onResize = () => this.renderer.resize();
    this.onPageHide = () => this.abandonActiveRunOnPageHide();
  }

  start() {
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('blur', this.onHidden);
    window.addEventListener('focus', this.onVisible);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('pagehide', this.onPageHide);
    this.bossAssets.load();
    if (window.visualViewport) window.visualViewport.addEventListener('resize', this.onResize);
    this.performSwitch('loading');
    this.loop.start(
      (dt) => {
        this.elapsed += dt;
        const vector = this.input.getKeyboardVector();
        if (this.currentScene && this.currentScene.handleKeyboard) {
          this.currentScene.handleKeyboard(dt, vector);
        }
        if (this.currentScene) this.currentScene.update(dt);
      },
      () => {
        this.renderer.beginFrame();
        this.renderer.drawBackground(this.elapsed);
        if (this.currentScene) this.currentScene.render(this.renderer.ctx);
      }
    );
  }

  stop() {
    this.loop.stop();
    this.input.destroy();
    this.feedback.destroy();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('blur', this.onHidden);
    window.removeEventListener('focus', this.onVisible);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('pagehide', this.onPageHide);
    if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.onResize);
  }

  setSyncStatus(message, pending) {
    if (!this.syncElement) return;
    this.syncElement.textContent = message;
    this.syncElement.classList.toggle('pending', Boolean(pending));
  }

  selectedCosmetic(type) {
    const dashboard = this.dashboard || {};
    const selection = dashboard.selection || {};
    const field = { PLANE: 'plane', TRAIL: 'trail', TITLE: 'title' }[type];
    const selectedKey = field && selection[field];
    return (dashboard.cosmetics || []).find((item) => item.key === selectedKey) || null;
  }

  cosmeticColor(type, fallback) {
    const item = this.selectedCosmetic(type);
    return item && item.color ? item.color : fallback;
  }

  async bootstrap() {
    try {
      await this.syncPendingRunFinishes();
      const [remote, dashboard] = await Promise.all([
        this.cloud.getPlayerProfile(),
        this.cloud.dashboard(),
        this.gameConfig.refresh()
      ]);
      remote.nickname = this.user.username;
      this.profile = this.storage.mergeProfiles(this.profile, remote);
      this.storage.saveProfile(this.profile);
      this.dashboard = dashboard;
      this.storage.saveDashboard(dashboard);
      this.offline = false;
      this.setSyncStatus('进度已同步', false);
      return true;
    } catch (error) {
      this.offline = true;
      this.setSyncStatus('进度待同步', true);
      return false;
    }
  }

  performSwitch(name, payload) {
    const next = this.scenes[name];
    if (!next) throw new Error(`Unknown scene: ${name}`);
    if (this.currentScene) this.currentScene.exit();
    this.currentScene = next;
    this.feedback.setMusicTrack(name === 'game' ? 'dodge' : name === 'boss' ? 'boss' : null);
    this.currentScene.enter(payload || {});
  }

  switchScene(name, payload) {
    if (name !== 'game') {
      this.performSwitch(name, payload);
      return;
    }
    const token = ++this.transitionToken;
    this.setSyncStatus('正在签发排名局…', true);
    this.prepareRun(payload && payload.level).then((runContext) => {
      if (token !== this.transitionToken) {
        if (runContext.ranked) this.abandonRun(runContext);
        return;
      }
      this.setSyncStatus(runContext.ranked ? '排名局已就绪' : '练习模式', !runContext.ranked);
      this.performSwitch(name, { ...(payload || {}), ...runContext });
    }).catch((error) => {
      if (token !== this.transitionToken) return;
      this.setSyncStatus(error.message || '无法开始对局', true);
    });
  }

  isNetworkError(error) {
    return !error || !Number.isInteger(error.status) || error.status === 0;
  }

  async prepareRun(level) {
    const normalizedLevel = Math.max(1, Math.min(99, Number(level) || 1));
    const [configResult, startResult] = await Promise.allSettled([
      this.gameConfig.refresh(),
      this.cloud.startRun(normalizedLevel, CLIENT_VERSION)
    ]);
    if (configResult.status === 'fulfilled') this.gameConfig.setPublished(configResult.value);
    if (startResult.status === 'fulfilled') {
      const started = startResult.value;
      const runContext = {
        ranked: true,
        runId: String(started.runId),
        configVersionId: started.configVersionId,
        startedAt: started.startedAt,
        level: normalizedLevel,
        config: started.levelConfig || this.gameConfig.getLevelConfig(normalizedLevel)
      };
      this.activeRunContext = runContext;
      return runContext;
    }
    if (!this.isNetworkError(startResult.reason)) throw startResult.reason;
    this.offline = true;
    return {
      ranked: false,
      runId: null,
      configVersionId: null,
      level: normalizedLevel,
      config: this.gameConfig.getLevelConfig(normalizedLevel)
    };
  }

  applySettlement(settlement) {
    if (!settlement || !settlement.progress) return;
    const remote = { ...settlement.progress, nickname: this.user.username };
    this.profile = this.storage.mergeProfiles(this.profile, remote);
    this.storage.saveProfile(this.profile);
  }

  queueRunFinish(runId, payload) {
    const value = {
      runId: String(runId), payload, queuedAt: new Date().toISOString(), userId: String(this.user.id)
    };
    const index = this.pendingRunFinishes.findIndex((item) => item.runId === value.runId);
    if (index >= 0) this.pendingRunFinishes[index] = value;
    else this.pendingRunFinishes.push(value);
    this.storage.savePendingRunFinishes(this.pendingRunFinishes);
  }

  async completeRun(runContext, payload) {
    if (!runContext || !runContext.ranked) {
      return {
        practice: true,
        run: {
          runId: null,
          level: Number(runContext && runContext.level) || 1,
          cleared: Boolean(payload.cleared),
          activeDurationMs: payload.activeDurationMs,
          score: null
        }
      };
    }
    this.clearActiveRun(runContext.runId);
    try {
      const settlement = await this.cloud.finishRun(runContext.runId, payload);
      this.applySettlement(settlement);
      this.refreshDashboard().catch(() => null);
      this.offline = false;
      this.setSyncStatus('成绩已同步', false);
      return settlement;
    } catch (error) {
      if (!this.isNetworkError(error)) throw error;
      this.queueRunFinish(runContext.runId, payload);
      this.offline = true;
      this.setSyncStatus('成绩待联网补交', true);
      return { queued: true, run: { runId: runContext.runId, level: runContext.level, cleared: payload.cleared, activeDurationMs: payload.activeDurationMs } };
    }
  }

  async abandonRun(runContext) {
    if (!runContext || !runContext.ranked || !runContext.runId) return false;
    this.clearActiveRun(runContext.runId);
    try {
      await this.cloud.abandonRun(runContext.runId);
      return true;
    } catch (error) {
      return false;
    }
  }

  clearActiveRun(runId) {
    if (
      this.activeRunContext
      && String(this.activeRunContext.runId) === String(runId)
    ) {
      this.activeRunContext = null;
    }
  }

  abandonActiveRunOnPageHide() {
    const run = this.activeRunContext;
    if (!run || !run.ranked || !run.runId) return;
    const url = `/api/runs/${encodeURIComponent(run.runId)}/abandon`;
    const body = new Blob(['{}'], { type: 'application/json' });
    const sent = navigator.sendBeacon && navigator.sendBeacon(url, body);
    if (!sent) {
      fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
        keepalive: true
      }).catch(() => null);
    }
    this.clearActiveRun(run.runId);
  }

  async refreshDashboard() {
    const dashboard = await this.cloud.dashboard();
    this.dashboard = dashboard;
    this.storage.saveDashboard(dashboard);
    return dashboard;
  }

  async refreshLeaderboard(type, period) {
    const leaderboard = await this.cloud.leaderboard(type, period, 100);
    this.leaderboard = leaderboard;
    this.storage.saveLeaderboard(leaderboard);
    return leaderboard;
  }

  async syncPendingRunFinishes() {
    if (this.syncing) return false;
    this.syncing = true;
    try {
      while (this.pendingRunFinishes.length) {
        const pending = this.pendingRunFinishes[0];
        try {
          const settlement = await this.cloud.finishRun(pending.runId, pending.payload);
          this.applySettlement(settlement);
          this.pendingRunFinishes.shift();
          this.storage.savePendingRunFinishes(this.pendingRunFinishes);
        } catch (error) {
          if (this.isNetworkError(error)) throw error;
          this.pendingRunFinishes.shift();
          this.storage.savePendingRunFinishes(this.pendingRunFinishes);
        }
      }
      this.offline = false;
      this.setSyncStatus('成绩已同步', false);
      return true;
    } catch (error) {
      this.offline = true;
      this.setSyncStatus('进度待同步', true);
      return false;
    } finally {
      this.syncing = false;
    }
  }
}

module.exports = App;
